import { useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { Link } from "react-router-dom";
import { HardHat, Quote, Heart, Share2, Trophy, Flame, Search } from "lucide-react";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";

const APPLAUSE_KEY = "ws_applauded_ids";

const PUBLIC_BASE =
  process.env.REACT_APP_PUBLIC_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

async function shareSubmission(submission) {
  const text = `“${submission.reason}” — ${submission.employee_name}`;
  const shareData = {
    title: "Why I Work Safe — worksafestory.com",
    text,
    url: `${PUBLIC_BASE}/story/${submission.id}`,
  };
  // Fire-and-forget share counter — never blocks the UX, never fails loudly.
  api.post(`/submissions/${submission.id}/share`).catch(() => null);
  if (navigator.share) {
    try {
      await navigator.share(shareData);
      return;
    } catch (err) {
      if (err?.name === "AbortError") return;
      logger.warn("[wall] navigator.share failed", err);
    }
  }
  try {
    await navigator.clipboard.writeText(`${text}\n${shareData.url}`);
    toast.success("Copied to clipboard — paste it anywhere.");
  } catch (err) {
    logger.warn("[wall] clipboard fallback failed", err);
    toast.error("Couldn't share. Try long-pressing the card to copy.");
  }
}

function readApplaudedSet() {
  try {
    const raw = localStorage.getItem(APPLAUSE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function persistApplaudedSet(set) {
  try {
    localStorage.setItem(APPLAUSE_KEY, JSON.stringify(Array.from(set)));
  } catch (err) {
    logger.warn("[wall] could not persist applause", err);
  }
}

function WallLoading() {
  return <div className="ws-label text-center py-20">Loading the wall…</div>;
}

function WallEmpty({ searchTerm }) {
  if (searchTerm) {
    return (
      <div
        className="border-2 border-dashed border-[#09090B] p-12 text-center"
        data-testid="wall-empty-no-results"
      >
        <Search strokeWidth={2.5} className="w-10 h-10 mx-auto text-[#FF5A00]" />
        <h3 className="font-display font-bold text-2xl mt-4">No stories match “{searchTerm}”.</h3>
        <p className="text-[#52525B] mt-2 max-w-md mx-auto">
          Try a different keyword, or clear the search to see every approved story.
        </p>
      </div>
    );
  }
  return (
    <div className="border-2 border-dashed border-[#09090B] p-12 text-center" data-testid="wall-empty">
      <Quote strokeWidth={2.5} className="w-10 h-10 mx-auto text-[#FF5A00]" />
      <h3 className="font-display font-bold text-2xl mt-4">Be the first to share a safety story</h3>
      <p className="text-[#52525B] mt-2 max-w-md mx-auto">
        Be the first to share why you work safe. Approved submissions show up here.
      </p>
      <Link
        to="/submit"
        className="inline-flex items-center gap-2 mt-6 bg-[#FF5A00] text-white font-bold px-6 py-3 ws-label border-2 border-[#09090B] ws-hard-shadow"
      >
        Submit Now →
      </Link>
    </div>
  );
}

function TrendingPill() {
  return (
    <div
      data-testid="trending-pill"
      className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 bg-[#FFCC00] text-[#09090B] border-2 border-[#09090B] px-3 py-1 ws-label ws-hard-shadow"
      title="Highest buzz this week — applause + shares"
    >
      <Trophy strokeWidth={2.5} className="w-3.5 h-3.5" />
      Trending
    </div>
  );
}

function WallCard({ submission, index, hasApplauded, onApplaud, isTrending }) {
  const photo = submission.photo_url;
  return (
    <article
      className="relative bg-white border-2 border-[#09090B] overflow-hidden ws-hard-shadow"
      data-testid={`wall-item-${index}`}
    >
      {isTrending && <TrendingPill />}
      <div className="aspect-[4/5] overflow-hidden border-b-2 border-[#09090B] bg-[#F4F4F5]">
        <img src={photo} alt={submission.employee_name} className="w-full h-full object-cover" />
      </div>
      <div className="p-6">
        <div className="ws-label text-[#FF5A00]">
          Reason · {String(index + 1).padStart(2, "0")}
        </div>
        <blockquote className="font-display font-bold text-xl tracking-tight mt-2 leading-snug">
          “{submission.reason}”
        </blockquote>
        <div className="mt-5 flex items-center justify-between border-t-2 border-dashed border-[#09090B] pt-4">
          <div className="flex items-center gap-2 text-sm text-[#52525B]">
            <HardHat strokeWidth={2.5} className="w-4 h-4" />
            <span className="font-semibold text-[#09090B]">{submission.employee_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => shareSubmission(submission)}
              data-testid={`share-${submission.id}`}
              aria-label="Share story"
              className="inline-flex items-center justify-center w-9 h-9 border-2 border-[#09090B] bg-white text-[#09090B] hover:bg-[#FFCC00] transition-colors"
            >
              <Share2 strokeWidth={2.5} className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onApplaud(submission.id)}
              disabled={hasApplauded}
              data-testid={`applaud-${submission.id}`}
              aria-pressed={hasApplauded}
              className={`group inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#09090B] ws-label transition-colors disabled:cursor-default ${
                hasApplauded
                  ? "bg-[#FF5A00] text-white"
                  : "bg-white text-[#09090B] hover:bg-[#FFCC00]"
              }`}
            >
              <Heart
                strokeWidth={2.5}
                className={`w-4 h-4 ${hasApplauded ? "fill-white" : "group-hover:fill-[#FF5A00] group-hover:text-[#FF5A00]"}`}
              />
              <span data-testid={`applause-count-${submission.id}`}>{submission.applause}</span>
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function WallGrid({ items, applauded, onApplaud, trendingId }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((it, i) => (
        <WallCard
          key={it.id}
          submission={it}
          index={i}
          hasApplauded={applauded.has(it.id)}
          onApplaud={onApplaud}
          isTrending={trendingId === it.id}
        />
      ))}
    </div>
  );
}

function SortToggle({ value, onChange }) {
  const baseClass = "text-xs font-bold tracking-[0.2em] uppercase px-4 py-2 border-2 border-[#09090B] transition-colors";
  return (
    <div className="inline-flex" data-testid="wall-sort-toggle">
      <button
        type="button"
        onClick={() => onChange("recent")}
        data-testid="sort-recent"
        className={`${baseClass} ${value === "recent" ? "bg-[#09090B] text-white" : "bg-white text-[#09090B] hover:bg-[#F4F4F5]"}`}
      >
        Most recent
      </button>
      <button
        type="button"
        onClick={() => onChange("applause")}
        data-testid="sort-applause"
        className={`${baseClass} border-l-0 ${value === "applause" ? "bg-[#09090B] text-white" : "bg-white text-[#09090B] hover:bg-[#F4F4F5]"}`}
      >
        Most loved
      </button>
    </div>
  );
}

function HallOfFameStrip({ stories, contributor }) {
  const hasStories = stories && stories.length > 0;
  if (!hasStories && !contributor) return null;
  return (
    <section
      className="border-b-2 border-[#09090B] bg-[#09090B] text-white"
      data-testid="hall-of-fame-strip"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center gap-3">
          <Trophy strokeWidth={2.5} className="w-6 h-6 text-[#FFCC00]" />
          <div className="ws-label text-[#FFCC00]">/ Hall of Fame · All time</div>
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2 leading-tight">
          THE MOST-SHARED STORIES.
        </h2>

        {contributor && (
          <div
            data-testid="top-contributor-callout"
            className="mt-8 flex items-center gap-5 bg-[#FF5A00] text-white border-2 border-[#FFCC00] p-5 ws-hard-shadow"
          >
            {contributor.photo_url ? (
              <img
                src={contributor.photo_url}
                alt={contributor.employee_name}
                className="w-16 h-16 object-cover border-2 border-[#09090B] flex-shrink-0"
              />
            ) : (
              <div className="w-16 h-16 bg-[#FFCC00] border-2 border-[#09090B] flex items-center justify-center flex-shrink-0">
                <HardHat strokeWidth={2.5} className="w-7 h-7 text-[#09090B]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="ws-label text-[#FFCC00]">Top contributor · All time</div>
              <div className="font-display font-black text-xl sm:text-2xl tracking-tight mt-0.5 truncate">
                {contributor.employee_name}
              </div>
              <div className="text-sm text-white/90 mt-1">
                <span className="font-bold">{contributor.submission_count}</span> approved {contributor.submission_count === 1 ? "story" : "stories"}
                {contributor.total_applause > 0 && (
                  <> · <span className="font-bold">{contributor.total_applause}</span> applause</>
                )}
              </div>
            </div>
          </div>
        )}

        {hasStories && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-8">
            {stories.map((s, i) => (
              <Link
                key={s.id}
                to={`/story/${s.id}`}
                data-testid={`hall-of-fame-${i}`}
                className="group block bg-white text-[#09090B] border-2 border-[#FFCC00] ws-hard-shadow hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform"
              >
                <div className="aspect-[5/3] overflow-hidden border-b-2 border-[#09090B] bg-[#F4F4F5] relative">
                  <img src={s.photo_url} alt={s.employee_name} className="w-full h-full object-cover" />
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#FFCC00] border-2 border-[#09090B] px-2 py-0.5 ws-label text-xs">
                    #{i + 1} · {s.buzz} buzz
                  </div>
                </div>
                <div className="p-4">
                  <blockquote className="font-display font-bold text-base leading-snug line-clamp-2">
                    “{s.reason}”
                  </blockquote>
                  <div className="mt-3 flex items-center gap-2 text-xs text-[#52525B]">
                    <HardHat strokeWidth={2.5} className="w-3.5 h-3.5" />
                    <span className="font-semibold text-[#09090B]">{s.employee_name}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function StreakChip({ days }) {
  if (!days || days < 1) return null;
  const label = days === 1 ? "day" : "days";
  return (
    <div
      data-testid="wall-streak-chip"
      className="inline-flex items-center gap-2 bg-[#09090B] text-[#FFCC00] border-2 border-[#09090B] px-3 py-2 ws-label ws-hard-shadow"
      title="Consecutive days with at least one approved story"
    >
      <Flame strokeWidth={2.5} className="w-4 h-4" />
      <span>Crew streak · {days} {label}</span>
    </div>
  );
}

function renderWallContent(loading, items, applauded, onApplaud, trendingId, searchTerm) {
  if (loading) return <WallLoading />;
  if (items.length === 0) return <WallEmpty searchTerm={searchTerm} />;
  return <WallGrid items={items} applauded={applauded} onApplaud={onApplaud} trendingId={trendingId} />;
}

export default function Wall() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("recent");
  const [applauded, setApplauded] = useState(() => readApplaudedSet());
  const [trendingId, setTrendingId] = useState(null);
  const [hallOfFame, setHallOfFame] = useState([]);
  const [topContributor, setTopContributor] = useState(null);
  const [streakDays, setStreakDays] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const PAGE_SIZE = 60;

  // Debounce the search input → reduces API chatter while typing.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ sort, offset: "0", limit: String(PAGE_SIZE) });
    if (search) params.set("q", search);
    api
      .get(`/submissions/wall?${params.toString()}`)
      .then((r) => {
        if (cancelled) return;
        const next = r.data || [];
        setItems(next);
        setHasMore(next.length === PAGE_SIZE);
      })
      .catch((err) => {
        if (cancelled) return;
        logger.warn("[wall] load failed", err);
        setItems([]);
        setHasMore(false);
      })
      .finally(() => (cancelled ? null : setLoading(false)));
    return () => {
      cancelled = true;
    };
  }, [sort, search]);

  useEffect(() => {
    let cancelled = false;
    // Trending pill is sort-independent — fetch once on mount.
    api
      .get("/submissions/wall/highlights")
      .then((r) => (cancelled ? null : setTrendingId(r.data?.top_buzz_id || null)))
      .catch((err) => logger.warn("[wall] highlights load failed", err));
    api
      .get("/submissions/wall/hall-of-fame")
      .then((r) => (cancelled ? null : setHallOfFame(r.data?.items || [])))
      .catch((err) => logger.warn("[wall] hall-of-fame load failed", err));
    api
      .get("/submissions/wall/top-contributor")
      .then((r) => (cancelled ? null : setTopContributor(r.data?.contributor || null)))
      .catch((err) => logger.warn("[wall] top-contributor load failed", err));
    api
      .get("/submissions/streak")
      .then((r) => (cancelled ? null : setStreakDays(r.data?.streak_days || 0)))
      .catch((err) => logger.warn("[wall] streak load failed", err));
    return () => {
      cancelled = true;
    };
  }, []);

  const handleApplaud = async (id) => {
    if (applauded.has(id)) return;
    // Optimistic UI; reconcile from server response.
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, applause: it.applause + 1 } : it)));
    const next = new Set(applauded);
    next.add(id);
    setApplauded(next);
    persistApplaudedSet(next);
    try {
      const { data } = await api.post(`/submissions/${id}/applause`);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, applause: data.applause } : it)));
    } catch (err) {
      logger.warn("[wall] applause failed", err);
      // Roll back optimistic increment if the request failed.
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, applause: Math.max(0, it.applause - 1) } : it)));
      const rolled = new Set(applauded);
      rolled.delete(id);
      setApplauded(rolled);
      persistApplaudedSet(rolled);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({
        sort,
        offset: String(items.length),
        limit: String(PAGE_SIZE),
      });
      if (search) params.set("q", search);
      const { data } = await api.get(`/submissions/wall?${params.toString()}`);
      const next = data || [];
      setItems((prev) => [...prev, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (err) {
      logger.warn("[wall] load-more failed", err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="bg-white min-h-screen text-[#09090B]">
      <SiteHeader />

      <section className="border-b-2 border-[#09090B] bg-[#FFCC00]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="ws-label">/ Wall of Safety</div>
          <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter mt-3 leading-[0.95]">
            EVERY FACE.<br />EVERY REASON.<br />ZERO HARM.
          </h1>
          <p className="mt-6 max-w-2xl text-[#09090B] text-lg leading-relaxed">
            Approved stories from across the company. Read them at shift start. Add yours so the next
            crew has one more reason to look out for each other.
          </p>
          <div className="mt-6 max-w-xl relative">
            <Search
              strokeWidth={2.5}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#09090B] pointer-events-none"
            />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or keyword (e.g. family, crane)"
              data-testid="wall-search-input"
              maxLength={120}
              className="w-full bg-white border-2 border-[#09090B] pl-10 pr-4 py-3 ws-label tracking-normal font-medium placeholder:text-[#52525B] placeholder:tracking-normal focus:outline-none focus:border-[#FF5A00]"
            />
          </div>
          <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
            <Link
              to="/submit"
              data-testid="wall-submit-cta"
              className="inline-flex items-center gap-2 bg-[#09090B] text-white font-bold px-8 py-4 text-xs tracking-[0.2em] uppercase border-2 border-[#09090B] ws-hard-shadow hover:bg-[#FF5A00] transition-colors"
            >
              Add Your Story →
            </Link>
            <div className="flex items-center gap-3 flex-wrap">
              <StreakChip days={streakDays} />
              <SortToggle value={sort} onChange={setSort} />
            </div>
          </div>
        </div>
      </section>

      <HallOfFameStrip stories={hallOfFame} contributor={topContributor} />

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        {renderWallContent(loading, items, applauded, handleApplaud, trendingId, search)}
        {hasMore && !loading && items.length > 0 && (
          <div className="mt-12 text-center">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="wall-load-more"
              className="inline-flex items-center gap-2 bg-white text-[#09090B] font-bold px-8 py-4 ws-label border-2 border-[#09090B] ws-hard-shadow hover:bg-[#FFCC00] transition-colors disabled:opacity-60"
            >
              {loadingMore ? "Loading…" : "Load More Stories →"}
            </button>
          </div>
        )}
      </section>

      <SiteFooter />
    </div>
  );
}
